import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Paper, 
  Divider, 
  List, 
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import RefreshIcon from '@mui/icons-material/Refresh';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorIcon from '@mui/icons-material/Error';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { 
  checkConnection, 
  resetConnectionState, 
  forceEnableNetwork,
  recoverFirestoreConnection,
  clearConnectionState,
  isOnboardingMode,
  setOnboardingMode
} from '../utils/firestoreConnectionManager';
import { useAuth } from '../context/AuthContext';
import { getAuth } from 'firebase/auth';

const FirestoreRetryPage = () => {
  const [checking, setChecking] = useState(false);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [logs, setLogs] = useState([]);
  const [isNewUser, setIsNewUser] = useState(false);
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Add a log entry
  const addLog = (message, type = 'info') => {
    setLogs(prev => [...prev, { message, type, timestamp: new Date() }]);
  };

  // Function to test connection
  const testConnection = async () => {
    if (retrying) return;
    
    setChecking(true);
    setError(null);
    addLog('Starting connection test', 'info');
    
    try {
      // First try to force-enable the network
      addLog('Attempting to enable network', 'info');
      await forceEnableNetwork();
      
      // Then check if we can connect
      addLog('Testing connection', 'info');
      const result = await checkConnection();
      console.log('Connection test result:', result);
      setConnected(result);
      addLog(`Connection test result: ${result ? 'Success' : 'Failed'}`, result ? 'success' : 'error');
      
      if (result) {
        // If connection is successful, reset connection state
        addLog('Resetting connection state', 'info');
        await resetConnectionState();
        addLog('Connection has been reset successfully', 'success');
        console.log('Connection has been reset successfully');
      } else {
        setError('Cannot connect to Firestore. Please try the recovery process.');
        addLog('Cannot connect to Firestore', 'error');
      }
    } catch (err) {
      console.error('Error testing connection:', err);
      setError(`Connection test failed: ${err.message}`);
      addLog(`Connection test error: ${err.message}`, 'error');
      setConnected(false);
    } finally {
      setChecking(false);
    }
  };

  // Function to perform basic recovery
  const performBasicRecovery = async () => {
    setRetrying(true);
    setError(null);
    setActiveStep(1);
    addLog('Starting basic recovery process', 'info');
    
    try {
      addLog('Attempting connection recovery', 'info');
      const result = await recoverFirestoreConnection();
      addLog(`Recovery result: ${result ? 'Success' : 'Failed'}`, result ? 'success' : 'error');
      
      if (result) {
        setConnected(true);
        addLog('Connection restored successfully', 'success');
      } else {
        setError('Basic recovery failed. Please try the advanced recovery.');
        addLog('Basic recovery failed', 'error');
      }
    } catch (err) {
      console.error('Error during basic recovery:', err);
      setError(`Recovery failed: ${err.message}`);
      addLog(`Recovery error: ${err.message}`, 'error');
    } finally {
      setRetrying(false);
    }
  };

  // Function to perform full recovery
  const performFullRecovery = async () => {
    setRetrying(true);
    setError(null);
    setActiveStep(2);
    addLog('Starting advanced recovery process', 'info');
    
    try {
      // Step 1: Clear all connection state
      addLog('Clearing connection state', 'info');
      const result = await clearConnectionState();
      addLog(`Clear state result: ${result ? 'Success' : 'Failed'}`, result ? 'success' : 'error');
      
      if (result) {
        // Step 2: Test the connection again
        addLog('Testing connection after clear', 'info');
        const testResult = await checkConnection();
        addLog(`Connection test result: ${testResult ? 'Success' : 'Failed'}`, testResult ? 'success' : 'error');
        setConnected(testResult);
        
        if (testResult) {
          // Success! Wait a moment before redirecting
          addLog('Connection fully restored', 'success');
          setTimeout(() => {
            if (currentUser) {
              navigate('/dashboard');
            } else {
              navigate('/login');
            }
          }, 2000);
        } else {
          setError('Connection could not be fully restored. Please reload the page.');
          addLog('Recovery not completely successful', 'warning');
        }
      } else {
        setError('Advanced recovery failed. Please try reloading the page.');
        addLog('Advanced recovery failed', 'error');
      }
    } catch (err) {
      console.error('Error during full recovery:', err);
      setError(`Advanced recovery failed: ${err.message}`);
      addLog(`Recovery error: ${err.message}`, 'error');
      setConnected(false);
    } finally {
      setRetrying(false);
    }
  };

  // Function to reload the page
  const reloadPage = () => {
    addLog('Reloading page', 'info');
    window.location.reload();
  };

  // Check connection on mount
  useEffect(() => {
    addLog('Component mounted, initiating connection test', 'info');
    testConnection();

    // Check if the user just signed up
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (user) {
      // If user was created less than 5 minutes ago, consider them a new user
      const fiveMinutesAgo = new Date();
      fiveMinutesAgo.setMinutes(fiveMinutesAgo.getMinutes() - 5);
      
      if (user.metadata && user.metadata.creationTime) {
        const creationTime = new Date(user.metadata.creationTime);
        if (creationTime > fiveMinutesAgo) {
          setIsNewUser(true);
        }
      }
    }
  }, []);

  // Define recovery steps
  const steps = [
    'Test Connection',
    'Basic Recovery',
    'Advanced Recovery'
  ];

  return (
    <Box sx={{ 
      maxWidth: 800, 
      mx: 'auto', 
      p: 3, 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: '100vh' 
    }}>
      <Typography variant="h4" gutterBottom>
        Connection Troubleshooter
      </Typography>
      
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {checking || retrying ? (
            <CircularProgress size={24} sx={{ mr: 2 }} />
          ) : connected ? (
            <CheckCircleIcon color="success" sx={{ mr: 2 }} />
          ) : (
            <WifiOffIcon color="error" sx={{ mr: 2 }} />
          )}
          
          <Typography variant="h6">
            {checking 
              ? 'Checking connection...' 
              : retrying
                ? 'Recovering connection...'
                : connected 
                  ? 'Connection restored!' 
                  : 'Connection issue detected'
            }
          </Typography>
        </Box>
        
        {(checking || retrying) && (
          <LinearProgress sx={{ mb: 2 }} />
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {connected && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Your connection to our database has been restored. You can now return to the app.
          </Alert>
        )}
        
        <Typography variant="body1" paragraph>
          This tool helps diagnose and fix issues with your connection to our database.
          If you're experiencing problems, try the recovery options below.
        </Typography>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={testConnection}
            disabled={checking || retrying}
          >
            {checking ? 'Checking...' : 'Test Connection'}
          </Button>
          
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<RestartAltIcon />}
            disabled={checking || retrying || connected}
            onClick={performBasicRecovery}
          >
            Basic Recovery
          </Button>
          
          <Button
            variant="outlined"
            color="warning"
            disabled={checking || retrying || connected}
            onClick={performFullRecovery}
          >
            Advanced Recovery
          </Button>
          
          <Button
            variant="outlined"
            onClick={reloadPage}
          >
            Reload Page
          </Button>
          
          {connected && (
            <Button
              variant="contained"
              color="success"
              onClick={() => navigate(currentUser ? '/dashboard' : '/login')}
            >
              Return to App
            </Button>
          )}
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        <Typography variant="subtitle1" gutterBottom>
          Connection Status:
        </Typography>
        
        <List>
          <ListItem>
            <ListItemIcon>
              {checking ? (
                <CircularProgress size={20} />
              ) : connected ? (
                <CheckCircleIcon color="success" />
              ) : (
                <ErrorIcon color="error" />
              )}
            </ListItemIcon>
            <ListItemText 
              primary="Database Connection" 
              secondary={checking 
                ? 'Checking...' 
                : connected 
                  ? 'Connected successfully' 
                  : 'Disconnected'
              } 
            />
          </ListItem>
        </List>
      </Paper>
      
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Diagnostic Logs</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ maxHeight: '200px', overflow: 'auto' }}>
          <List dense>
            {logs.map((log, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  {log.type === 'success' && <CheckCircleIcon color="success" fontSize="small" />}
                  {log.type === 'error' && <ErrorIcon color="error" fontSize="small" />}
                  {log.type === 'warning' && <ErrorIcon color="warning" fontSize="small" />}
                  {log.type === 'info' && <RefreshIcon color="info" fontSize="small" />}
                </ListItemIcon>
                <ListItemText 
                  primary={log.message} 
                  secondary={log.timestamp.toLocaleTimeString()} 
                />
              </ListItem>
            ))}
            {logs.length === 0 && (
              <ListItem>
                <ListItemText primary="No logs available" />
              </ListItem>
            )}
          </List>
        </AccordionDetails>
      </Accordion>
      
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Troubleshooting Tips</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <List>
            <ListItem>
              <ListItemText primary="Check your internet connection" secondary="Make sure you have a stable internet connection" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Try refreshing the page" secondary="Sometimes a simple refresh can fix connection issues" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Clear browser cache" secondary="Clearing your browser cache can resolve persistent issues" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Try a different browser" secondary="If problems persist, try using a different web browser" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Check Firebase status" secondary="The issue might be with Firebase itself, check their status page" />
            </ListItem>
          </List>
        </AccordionDetails>
      </Accordion>

      {isNewUser && (
        <Alert severity="info" sx={{ mt: 3, mb: 2 }}>
          It looks like you just signed up! You can continue to set up your account without waiting.
          <Box sx={{ mt: 2 }}>
            <Button 
              variant="contained" 
              color="primary"
              onClick={() => {
                setOnboardingMode(true);
                navigate('/onboarding');
              }}
            >
              Continue to Account Setup
            </Button>
          </Box>
        </Alert>
      )}
    </Box>
  );
};

export default FirestoreRetryPage; 